import { useAuth } from "@clerk/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";

import { ApiClient, UnauthorizedError } from "../lib/api";

const SUBSCRIPTION_QUERY_KEY = ["subscription"];
const SUBSCRIPTION_POLL_MS = 60_000;

export function useSubscription() {
	const { getToken, isSignedIn, signOut, userId } = useAuth();
	const client = new ApiClient(getToken);
	const queryClient = useQueryClient();

	const query = useQuery({
		queryKey: ["subscription", userId],
		queryFn: () => client.getSubscription(),
		enabled: Boolean(isSignedIn && userId),
		refetchInterval: SUBSCRIPTION_POLL_MS,
		retry: (failureCount, error) => {
			if (error instanceof UnauthorizedError) return false;
			return failureCount < 3;
		},
	});

	useEffect(() => {
		if (query.error instanceof UnauthorizedError) {
			void signOut();
		}
	}, [query.error, signOut]);

	useEffect(() => {
		const handlePaymentRequired = () => {
			void queryClient.invalidateQueries({ queryKey: SUBSCRIPTION_QUERY_KEY });
		};

		window.addEventListener("kb:payment-required", handlePaymentRequired);
		return () =>
			window.removeEventListener("kb:payment-required", handlePaymentRequired);
	}, [queryClient]);

	return { ...query, active: query.data?.active ?? false };
}

export function useCheckout() {
	const { getToken } = useAuth();
	const client = new ApiClient(getToken);

	return useMutation({
		mutationFn: () => client.createCheckoutSession(),
		onSuccess: ({ url }) => {
			window.location.assign(url);
		},
	});
}

export function useBillingPortal() {
	const { getToken } = useAuth();
	const client = new ApiClient(getToken);

	return useMutation({
		mutationFn: () => client.createBillingPortalSession(),
		onSuccess: ({ url }) => {
			window.location.assign(url);
		},
	});
}
